import { Button } from "@rosette/web_components/src/ui/Button.js";
import type { ReactNode } from "react";
import React, { startTransition, useCallback, useState } from "react";
import type { APIOptionalUser } from "@rosette/ts/src/business/api/api_schema.js";
import { toCompleteUser } from "@rosette/ts/src/business/api/api_schema.js";
import type { GameClientControls } from "@rosette/ts/src/business/GameClientControls.js";
import type { KnownLobbySettings } from "@rosette/ts/src/business/game/KnownLobbySettings.js";
import { loadNudge } from "@rosette/web_utils/src/local/nudgeStorage.js";
import { NudgeType } from "@rosette/ts/src/business/nudge/NudgeType.js";
import { NudgeCheckResult } from "@rosette/ts/src/business/nudge/NudgeCheckResult.js";
import { checkSuggestPandaBotNudge } from "@rosette/ts/src/business/nudge/suggestPandaBotNudge.js";
import { NudgeAnalyticsEvent } from "@rosette/ts/src/business/analytics/NudgeAnalyticsEvent.js";
import { AnalyticsEventType } from "@rosette/ts/src/business/analytics/AnalyticsEventType.js";
import { matomo } from "@/components/Matomo.js";
import { SuggestPandaBotModal } from "@/components/nudge/SuggestPandaBotModal.js";
import { BotType } from "@rosette/ts/src/business/game/BotType.js";
import { logDebug } from "@rosette/ts/src/business/debug.js";
import { SheenText, useSheen } from "@/components/generic/sheen/SheenText.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { useBreathe } from "@/components/generic/sheen/useBreathe.js";


// The quick-play button rests with a faint glow and breathes now and
// then (useBreathe). With reduced motion it keeps a stronger, still glow.
// An animated box-shadow beats every shadow utility, so a disabled button
// must drop the animation to lose its glow.
const QUICKPLAY_GLOW_CLASSES = cn(
    "shadow-[0_0_12px_color-mix(in_oklab,var(--color-marigold-500)_25%,transparent),0_4px_6px_-1px_rgb(0_0_0/0.1),0_2px_4px_-2px_rgb(0_0_0/0.1)]",
    "motion-reduce:shadow-[0_0_24px_color-mix(in_oklab,var(--color-marigold-500)_45%,transparent),0_4px_6px_-1px_rgb(0_0_0/0.1),0_2px_4px_-2px_rgb(0_0_0/0.1)]",
    "motion-reduce:disabled:shadow-none",
);
const QUICKPLAY_BREATHE_CLASSES = "animate-quickplay-breathe disabled:animate-none";


function playOpponentLabel(botType: BotType): string {
    if (botType === BotType.PANDA)
        return "Play the Panda";

    return `Play the ${botType.getLongName()}`;
}


interface SecondaryButtonProps {
    href: string;
    onPointerEnter?: () => void;
    onPointerLeave?: () => void;
    children: ReactNode;
}

function SecondaryButton({ href, onPointerEnter, onPointerLeave, children }: SecondaryButtonProps) {
    return (
        <Button
            tone="primary"
            className="group flex w-full items-center justify-center"
            href={href}
            onPointerEnter={onPointerEnter}
            onPointerLeave={onPointerLeave}>

            {children}
        </Button>
    );
}


interface PremiumButtonProps {
    diamond: boolean;
    sheenCount: number;
    triggerSheen: () => void;
}

function PremiumButton({ diamond, sheenCount, triggerSheen: trigger }: PremiumButtonProps) {
    return (
        <SecondaryButton href="/premium" onPointerEnter={trigger} onPointerLeave={trigger}>
            <SheenText sheenCount={sheenCount} sheenClassName="text-white group-hover:text-on-primary">
                {diamond ? "Get Diamond" : "Go Premium"}
            </SheenText>
        </SecondaryButton>
    );
}


interface HomePlayButtonsProps {
    clientControls: GameClientControls;
    user: APIOptionalUser;
    gameSettings: KnownLobbySettings | null;
    newUserMode: boolean;
    shouldSuggestPandaBot: boolean;
}


export function HomePlayButtons({
    clientControls, user, gameSettings, newUserMode, shouldSuggestPandaBot,
}: HomePlayButtonsProps) {
    const completeUser = toCompleteUser(user);
    const showPremium = !!completeUser && !completeUser.hasRoleMembur;
    const sheen = useSheen(showPremium);
    const breathing = useBreathe(sheen.isClearFor);
    const [clickedPlay, setClickedPlay] = useState(false);
    const [showPandaNudge, setShowPandaNudge] = useState(false);

    const startGame = useCallback((settings: KnownLobbySettings | null) => {
        if (!settings)
            return;

        startTransition(() => {
            setClickedPlay(true);
            clientControls.startGame(settings);
        });
    }, [setClickedPlay, clientControls]);

    const onPlayClick = useCallback(() => {
        if (!gameSettings) {
            logDebug("Panda nudge: Play button clicked but no game settings");
            return;
        }

        logDebug("Panda nudge: Play button clicked", {
            shouldSuggestPandaBot,
            gameMode: gameSettings.getMode(),
            botType: gameSettings.getBotType(),
        });

        if (shouldSuggestPandaBot) {
            const nudge = loadNudge(NudgeType.SUGGEST_PANDA_BOT);
            const nudgeResult = checkSuggestPandaBotNudge(nudge);
            logDebug("Panda nudge: Rechecking nudge on play button click", {
                nudgeResult,
                consecutiveAccepts: nudge.getConsecutiveAccepts(),
                consecutiveDeclines: nudge.getConsecutiveDeclines(),
                skipsSinceLastShown: nudge.getSkipsSinceLastShown(),
            });
            if (nudgeResult === NudgeCheckResult.SHOW) {
                nudge.markShown();
                matomo.recordEvent(new NudgeAnalyticsEvent(
                    AnalyticsEventType.NUDGE_SHOWN,
                    NudgeType.SUGGEST_PANDA_BOT,
                ));
                logDebug("Panda nudge: Showing panda bot nudge modal");
                setShowPandaNudge(true);
                return;
            }
        }
        logDebug("Panda nudge: Starting game without showing nudge");
        startGame(gameSettings);
    }, [gameSettings, shouldSuggestPandaBot, setShowPandaNudge, startGame]);

    const onAcceptPandaNudge = useCallback(() => {
        setShowPandaNudge(false);
        const nudge = loadNudge(NudgeType.SUGGEST_PANDA_BOT);
        nudge.markAccepted();

        matomo.recordEvent(new NudgeAnalyticsEvent(
            AnalyticsEventType.NUDGE_ACCEPTED,
            NudgeType.SUGGEST_PANDA_BOT,
        ));

        startGame(gameSettings ? gameSettings.withBotType(BotType.PANDA) : null);
    }, [setShowPandaNudge, gameSettings, startGame]);

    const onClosePandaNudge = useCallback(() => {
        setShowPandaNudge(false);
        const nudge = loadNudge(NudgeType.SUGGEST_PANDA_BOT);
        nudge.markDismissed();

        matomo.recordEvent(new NudgeAnalyticsEvent(
            AnalyticsEventType.NUDGE_DISMISSED,
            NudgeType.SUGGEST_PANDA_BOT,
        ));
    }, [setShowPandaNudge]);

    const onRejectPandaNudge = useCallback(() => {
        onClosePandaNudge();
        startGame(gameSettings);
    }, [onClosePandaNudge, gameSettings, startGame]);

    let secondaryButton: ReactNode = null;
    if (!user && !newUserMode) {
        secondaryButton = <SecondaryButton href="/register">Register</SecondaryButton>;
    } else if (user && !completeUser) {
        secondaryButton = <SecondaryButton href="/setup">Finish sign-up</SecondaryButton>;
    } else if (showPremium) {
        secondaryButton = (
            <PremiumButton
                diamond={completeUser.hasRoleGold}
                sheenCount={sheen.sheenCount}
                triggerSheen={sheen.trigger} />
        );
    }

    let playButtonLabel = "\xA0";
    if (gameSettings) {
        playButtonLabel = (
            newUserMode
                ? "Play now"
                : playOpponentLabel(gameSettings.getBotType())
        );
    }
    return (
        <div className="flex flex-col gap-6 w-full max-w-xl">
            <Button
                tone="accent"
                size="lg"
                className={cn(
                    "w-full",
                    QUICKPLAY_GLOW_CLASSES,
                    breathing ? QUICKPLAY_BREATHE_CLASSES : null,
                )}
                onClick={onPlayClick}>

                {(() => {
                    if (clickedPlay) {
                        if (clientControls.startingGame)
                            return "...";
                        if (clientControls.startGameError)
                            return "Error: " + clientControls.startGameError.message;
                    }
                    return playButtonLabel;
                })()}
            </Button>

            <div className="flex flex-col md:flex-row gap-4 w-full px-3">
                <SecondaryButton href="/game">Custom game</SecondaryButton>
                {secondaryButton}
            </div>

            <SuggestPandaBotModal
                visible={showPandaNudge}
                onAccept={onAcceptPandaNudge}
                onReject={onRejectPandaNudge}
                onClose={onClosePandaNudge} />
        </div>
    );
}
